import './index.css'
import cn from 'clsx'

import React from 'react'
import Link from 'components/ui/link'
import TruncateOnHover from 'components/ui/truncate-on-hover'
import Overlay from './overlay'
export type LinkComponentType = {
  external?: boolean
  href: string
  // Deprecated
  as?: string
}
type Props = {
  caption?: React.ReactNode
  description?: React.ReactNode
  link?: LinkComponentType
  media: React.ReactNode
  tagline?: React.ReactNode
  title: React.ReactNode
  onClick?: (...args: Array<any>) => any
}

/**
 * A grid item component that displays media, title, and optional content with link or click functionality
 * @param caption - Additional caption text displayed below the title
 * @param description - Detailed description text with truncation support
 * @param link - Link configuration object with href and optional external/as properties
 * @param media - The media content (image, icon, etc.) to display
 * @param tagline - Tagline text displayed below the caption
 * @param title - The main title content for the grid item
 * @param onClick - Click handler function for the grid item
 * @returns A grid item component that can be a clickable link or button
 */
const GridItem = ({
  caption,
  description,
  link,
  media,
  tagline,
  title,
  onClick,
}: Props): React.ReactElement<string> => {
  let Component: React.ElementType = 'div'
  let linkProps = {}

  if (link) {
    Component = link.external ? 'a' : Link
    linkProps = {
      href: link.href,
      // Deprecated
      as: link.as,
    }

    if (link.external) {
      linkProps = { ...linkProps, rel: 'noopener noreferrer', target: '_blank' }
    }
  }

  let tooltip = typeof title === 'string' ? title : undefined

  const content = (
    <>
      <div className="GridItem-media">{media}</div>
      <div className="GridItem-content">
        {caption ? (
          <div
            className="GridItem-title GridItem-title--truncated"
            title={tooltip}
            translate="no"
          >
            {title}
          </div>
        ) : (
          <div
            className="GridItem-title GridItem-title--multiline"
            title={tooltip}
            translate="no"
          >
            {title}
          </div>
        )}
        {caption && <div className="GridItem-caption">{caption}</div>}
        {tagline && <div className="GridItem-tagline">{tagline}</div>}
        {description && (
          <div className="GridItem-description">
            <TruncateOnHover maxLines={3}>{description}</TruncateOnHover>
          </div>
        )}
      </div>
    </>
  )

  const gridItemClassName = cn('GridItem', {
    'GridItem--clickable': link || onClick,
  })

  // When using Link component, wrap content in a single div to satisfy Next.js Link requirement
  // Next.js Link only accepts a single child, but other components (div, a) can have multiple
  // Apply GridItem classes to the child div (Next.js Link passes props to child, not wrapper)
  if (link && !link.external) {
    return (
      <Component {...linkProps}>
        <div className={gridItemClassName} onClick={onClick}>
          {content}
        </div>
      </Component>
    )
  }

  return (
    <Component className={gridItemClassName} onClick={onClick} {...linkProps}>
      {content}
    </Component>
  )
}

GridItem.Overlay = Overlay
export default GridItem
